import type { FC } from 'react';
import React from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import Image from '@atlaskit/image';
import EmptyState from '@atlaskit/empty-state';
import Button from '@atlaskit/button/standard-button';

import EndOfFeedImageLight from './images/EndOfFeedLight.svg';
import EndOfFeedImageDark from './images/EndOfFeedDark.svg';

export const i18n = defineMessages({
	header: {
		id: 'feed.end-of-feed.header',
		defaultMessage: 'No more activity',
		description:
			'header message shown when a user gets to the end of their feed (no more items to show them)',
	},
	description: {
		id: 'feed.end-of-feed.description',
		defaultMessage: 'Check back later or refine your feed for more updates',
		description:
			'description message shown when a user gets to the end of their feed (no more items to show them)',
	},
	buttonText: {
		id: 'feed.end-of-feed.button.text',
		defaultMessage: 'Edit feed',
		description:
			'action button on error message that opens a modal that allows the user to change settings that effect the contents of the feed.',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EndOfFeedImage = styled(Image)({
	maxWidth: '74px',
	maxHeight: '74px',
});

type Props = {
	openActivityFilterModal?: () => void;
} & WrappedComponentProps;

const EndOfFeedComponent: FC<Props> = ({ intl, openActivityFilterModal }) => {
	const actionButton = (
		<Button onClick={openActivityFilterModal}>
			<FormattedMessage {...i18n.buttonText} />
		</Button>
	);

	return (
		<div data-testid="end-of-feed-message">
			<EmptyState
				header={intl.formatMessage(i18n.header)}
				description={openActivityFilterModal ? intl.formatMessage(i18n.description) : undefined}
				primaryAction={openActivityFilterModal ? actionButton : undefined}
				renderImage={() => (
					<EndOfFeedImage
						src={EndOfFeedImageLight}
						srcDark={EndOfFeedImageDark}
						width="74"
						height="74"
						alt=""
					/>
				)}
				imageHeight={74}
			/>
		</div>
	);
};

export const EndOfFeed = injectIntl(EndOfFeedComponent);
